import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';
import { ActionService, Extra } from 'src/app/core/services/action.service';
import { RouteService } from 'src/app/core/services/route.service';
import { ForwardEmail, LoadNotificationLog, ResendEmail } from 'src/app/core/state/_actions/settings/audit-log.action';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import { getAuditLogOperations, getNotificationLogDetails } from 'src/app/core/state/_selectors/settings/audit-log.selector';
import { alertPresets } from 'src/app/shared/services/alert.service';
import { SubSink } from 'subsink';
import { SweetAlertOptions } from 'sweetalert2';

@Component({
  selector: 'app-email-template',
  templateUrl: './email-template.component.html',
  styleUrls: ['./email-template.component.scss']
})
export class EmailTemplateComponent implements OnInit {
  @Input() id:any;
  @Input() tableId:any;
  emailData:any;
  @ViewChild('printableModalContent') printableContent!: ElementRef;
  form!:UntypedFormGroup;
  isResendActive:boolean = false;
  isForwardActive:boolean = false;
  isTransformationDone:boolean = false;
  isValuePatched:boolean = false;
  forwardToPattern = /^[\w+-]+(\.[\w+-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}(;\s*[\w+-]+(\.[\w+-]+)*@([\w-]+\.)+[a-zA-Z]{2,7})*$/;
  defaultExtra: Extra = {
    success: {
      show: true,
      message: '',
      title: '',
      showSwal: false,
      alertOptions: <SweetAlertOptions>alertPresets.saveSuccess,
    },
    error: {
      show: true,
      title: '',
      showResponseError: true,
    },
  };
  subs = new SubSink();

  constructor(
    private modal:NgbModal,
    private fb:UntypedFormBuilder,
    private action:ActionService,
    private route:RouteService,
    private store:Store
  ) { }

  ngOnInit(): void {
    this.store.dispatch(new LoadNotificationLog(this.route.getApplicationKey(),this.tableId,this.id))
    this.initForm();
    this.loadSubscribers();
  }

  initForm(){
    this.form = this.fb.group({
      ToAddress: ['',Validators.required],
      Subject: ['',Validators.required],
      Message: ['',Validators.required],
      Comments: [''],
      ForwardTo: [''],
      FromAddress: [''],
    })
  }

  loadSubscribers(){
    this.subs.sink = this.store.pipe(
      select(getNotificationLogDetails),
      filter((data) => !!data && !!data[this.route.getApplicationKey()] && !!data[this.route.getApplicationKey()][this.tableId] && !!data[this.route.getApplicationKey()][this.tableId][this.id]  && this.id),
      map((detail) => detail[this.route.getApplicationKey()][this.tableId][this.id])
    ).subscribe(detail => {
      this.emailData = detail;
      this.form.patchValue({
        ToAddress: this.emailData.toaddress,
        Subject: this.emailData.subject,
        Message: this.emailData.message,
        FromAddress: this.emailData.fromaddress,
      })
    })
  }

  close(){
    this.modal.dismissAll();
  }

  submit = async() => {
    if(this.isResendActive){
      await this.action.dispatch(
        OpTypes.CREATE,
        new ResendEmail(this.getPayload()),
        getAuditLogOperations,
        {
          ...this.defaultExtra,
          success: { 
            ...this.defaultExtra.success,
            show:true, 
            title:'Success',
            message: 'Email sent successfully.'
          }
        }
      )
    } else {
      await this.action.dispatch(
        OpTypes.CREATE,
        new ForwardEmail(this.getPayload()),
        getAuditLogOperations,
        {
          ...this.defaultExtra,
          success: { 
            ...this.defaultExtra.success,
            show:true, 
            title:'Success',
            message: 'Email sent successfully.'
          }
        }
      )
    }
    this.close();
  }

  getPayload(){
    if(this.isResendActive){
      return {
        ApplicationKey:this.route.getApplicationKey(),
        ToAddress: this.form.controls.ToAddress.value,
        Subject: this.form.controls.Subject.value,
        Message: this.form.value.Comments ? `<div>${this.form.value.Comments}</div>${this.form.value.Message}` : this.form.value.Message,
        NotificationId:this.emailData.notificationId,
        NotificationKey:this.emailData.notificationKey,
      }
    } else{
      return {
        ApplicationKey:this.route.getApplicationKey(),
        ForwardAddress: this.form.controls.ForwardTo.value,
        Subject: this.form.controls.Subject.value,
        Message: this.form.value.Comments ? `<div>${this.form.controls.ToAddress.value}</div><div>${this.form.value.Comments}</div>${this.form.value.Message}` : `<div>${this.form.controls.ToAddress.value}</div>${this.form.value.Message}`,
        NotificationId:this.emailData.notificationId,
        NotificationKey:this.emailData.notificationKey,
      }
    }
  }

  performEmailOperation(operation:string){
    switch(operation){
      case 'Resend':
        this.isResendActive = true;
        this.form.controls.Comments.reset();
        this.form.controls.ForwardTo.clearValidators();
        this.form.controls.ForwardTo.updateValueAndValidity();
        break;
        case 'Forward':
          this.isForwardActive = true;
          this.form.controls.Comments.reset();
          this.form.controls.ForwardTo.reset();
        this.form.controls.ForwardTo.addValidators([Validators.required,Validators.pattern(this.forwardToPattern)]);
        this.form.controls.ForwardTo.updateValueAndValidity();
        break;
      case 'Print':
        this.isResendActive = false;
        this.isForwardActive = false;
        this.printModalContent();
        break;
      case 'Export':
        this.isResendActive = false;
        this.isForwardActive = false;
        this.saveAsHtml();
        break;
    }
  }

  transformObject(obj:any) {
    return Object.entries(obj).reduce((acc:any, [key, value]) => {
      const newKey = key.replace(/^tbl\d+_/, ''); // Remove 'tbl{number}_' prefix
      acc[newKey] = value; // Overwrite any duplicate keys
      return acc;
    }, {});
  }

  cancelEmailOperation(){
    if(this.isResendActive){
      this.form.controls.Comments.reset();
      this.isResendActive = false;
    } else if(this.isForwardActive){
      this.isForwardActive = false;
      this.form.controls.Comments.reset();
      this.form.controls.ForwardTo.reset();
      this.form.controls.ForwardTo.clearValidators();
      this.form.controls.ForwardTo.updateValueAndValidity();
    }
  }

  printModalContent() {
    let printContents = this.printableContent.nativeElement.innerHTML;
    printContents = this.replaceFormControlsWithValues(printContents);
    const popupWindow = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    if (popupWindow) {
      popupWindow.document.open();
      popupWindow.document.write(`
        <html>
          <head>
            <title>${this.form.controls.Subject.value}</title>
            <style>
              ${this.getStyles()}
            </style>
          </head>
          <body onload="window.print(); window.close();">${printContents}</body>
        </html>
      `);
      popupWindow.document.close();
    }
  }

  saveAsHtml() {
    let content = this.printableContent.nativeElement.innerHTML;
    content = this.replaceFormControlsWithValues(content)
    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        ${this.getStyles()}
      </style>
    </head>
    <body>
      ${content}
    </body>
    </html>
  `;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.form.controls.Subject.value}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  replaceFormControlsWithValues(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
  
    const inputs = tempDiv.querySelectorAll('input, textarea, select');
    inputs.forEach((input,index) => {
      const value = (index === 0) ? this.form.controls.ToAddress.value : this.form.controls.Subject.value;
      const span = document.createElement('span');
      span.textContent = value;
      span.style.fontSize = '12px';
      input.parentNode?.replaceChild(span, input);
    });

    const rowDivs = tempDiv.querySelectorAll('div.row');
    rowDivs.forEach((rowDiv, index) => {
      if ((index + 1) % 2 === 0) {
        const newRowDiv = this.createCustomRowDiv();
        rowDiv.insertAdjacentElement('afterend', newRowDiv);
      }
    });
  
    return tempDiv.innerHTML;
  }

  createCustomRowDiv(): HTMLElement {
    const newRowDiv = document.createElement('div');
    newRowDiv.classList.add('row');
    const colDiv = document.createElement('div');
    colDiv.classList.add('col');
    const label = document.createElement('label');
    label.textContent = 'From';
    label.style.display = 'block'
    const span = document.createElement('span');
    span.textContent = this.form.controls.FromAddress.value;
    span.style.fontSize = '12px';
    colDiv.appendChild(label);
    colDiv.appendChild(span);
    colDiv.style.marginBottom = '12px'
    newRowDiv.appendChild(colDiv);
    return newRowDiv;
  }

  getStyles(): string {
    let styles = '';
    for (const styleSheet of Array.from(document.styleSheets)) {
      try {
        if (styleSheet.cssRules.length) {
          for (const rule of Array.from(styleSheet.cssRules)) {
            styles += rule.cssText;
          }
        }
      } catch (e) {
      }
    }
    return styles;
  }

  ngOnDestroy(){
    this.subs.unsubscribe();
  }
}
